import React, { useCallback, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import PostModel from "app/models/PostModel";
import Button from "app/components/Button/ButtonVariant";
import { cls } from "app/utils";
import TagModel from "app/models/TagModel";
import TagsSelect from "app/components/inputs/SelectBox/TagsSelect";
import { useTranslation } from "react-i18next";
import ScheduleBtn from "app/components/Button/ScheduleBtn";
import ScheduledAtPicker from "app/components/ScheduledPosts/ScheduledAtPicker";
import { toInteger } from "lodash";
import { useLocation } from "react-router-dom";

interface EditPostProps {
  classNames: string;
  post: PostModel;
  onUpdate: (post: PostModel) => void;
  onCancel: () => void;
  editTags?: boolean;
  hideSelectTags?: boolean;
}

const TIME_CONSTANTS = {
  HOURS_PER_DAY: 24,
  HOURS_PER_WEEK: 168,
} as const;

type TimeUnit = "Hour" | "Day" | "Week";
type ScheduleTimeUnit = "Hours" | "Days" | "Weeks";

const timeUtils = {
  convertHoursToDisplayUnit: (
    hours: number,
  ): { value: number; unit: TimeUnit } => {
    if (hours < TIME_CONSTANTS.HOURS_PER_DAY) {
      return { value: hours, unit: "Hour" };
    }

    if (hours < TIME_CONSTANTS.HOURS_PER_WEEK) {
      return { value: hours / TIME_CONSTANTS.HOURS_PER_DAY, unit: "Day" };
    }

    return { value: hours / TIME_CONSTANTS.HOURS_PER_WEEK, unit: "Week" };
  },

  /**
   * Converts a time value and unit back to hours
   */
  convertToHours: (value: number, unit: ScheduleTimeUnit): number => {
    const intValue = toInteger(value);

    switch (unit) {
      case "Days":
        return intValue * TIME_CONSTANTS.HOURS_PER_DAY;
      case "Weeks":
        return intValue * TIME_CONSTANTS.HOURS_PER_WEEK;
      case "Hours":
      default:
        return intValue;
    }
  },
};

const EditPost = ({
  classNames,
  post,
  onUpdate,
  onCancel,
  editTags,
  hideSelectTags,
}: EditPostProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isTemplatePage] = useState(
    location.pathname.includes("space_templates"),
  );
  const [editedPost, setEditedPost] = useState<PostModel | null>(
    (post ??= null),
  );

  const [scheduledTime, setScheduledTime] = useState<string>("");
  const [scheduledValue, setScheduledValue] = useState<number>(
    editedPost?.scheduledIn ?? 0,
  );

  useEffect(() => {
    const scheduledHours = editedPost?.scheduledIn;

    if (!scheduledHours || scheduledHours <= 0) {
      setScheduledTime("");
      setScheduledValue(0);
      return;
    }

    const { value, unit } = timeUtils.convertHoursToDisplayUnit(scheduledHours);
    setScheduledValue(value);
    setScheduledTime(unit);
  }, [editedPost?.scheduledIn]);

  const onTagsChange = useCallback((tags: TagModel[]) => {
    setEditedPost((prev) => ({
      ...prev,
      tags: tags ? tags.map((t) => t.name) : [],
    }));
  }, []);

  const onDetailsChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = event.target;
      setEditedPost((prevData) => ({
        ...prevData,
        details: value,
      }));
    },
    [],
  );

  const onScheduleChange = useCallback(
    (pickedValue: number, pickedTime?: ScheduleTimeUnit) => {
      if (!pickedValue) {
        return;
      }

      setEditedPost((prevData) => {
        if (isTemplatePage) {
          return handleTemplateScheduling(prevData, pickedValue, pickedTime);
        } else {
          return handleAbsoluteScheduling(prevData, pickedValue);
        }
      });
    },
    [isTemplatePage],
  );

  // Helper function for template page scheduling
  const handleTemplateScheduling = (
    prevData: PostModel | null,
    pickedValue: number,
    pickedTime?: ScheduleTimeUnit,
  ): PostModel | null => {
    if (!pickedTime) {
      return prevData;
    }

    const hoursValue = timeUtils.convertToHours(pickedValue, pickedTime);

    setScheduledValue(pickedValue);
    setScheduledTime(pickedTime.slice(0, -1) as TimeUnit);

    return {
      ...prevData,
      scheduled_in: hoursValue,
    };
  };

  const handleAbsoluteScheduling = (
    prevData: PostModel | null,
    timestamp: number,
  ): PostModel | null => {
    return {
      ...prevData,
      scheduled_at: new Date(timestamp).toISOString(),
    };
  };

  const handleUpdate = useCallback(() => {
    if (editedPost) {
      onUpdate(editedPost);
      onCancel();
    }
  }, [editedPost, onUpdate, onCancel]);

  return (
    <div className={cls(styles.container, classNames)}>
      {!editTags && (
        <textarea
          placeholder={t("Say something...")}
          value={editedPost?.details}
          onChange={onDetailsChange}
          rows={4}
        />
      )}
      {!hideSelectTags && (
        <TagsSelect
          isCreatable
          isClearable
          isMulti
          onChange={onTagsChange}
          value={editedPost?.tags}
          tagValContainer={styles.tag_container}
        />
      )}
      <div
        className={cls({
          [styles.justify_between]: isTemplatePage || editedPost?.scheduledAt,
        })}
      >
        {!editTags && isTemplatePage && (
          <ScheduleBtn
            onScheduleChange={onScheduleChange}
            scheduledAt={scheduledValue}
            scheduledTime={scheduledTime}
          />
        )}
        {!editTags && new Date(editedPost?.scheduledAt) > new Date() && (
          <ScheduledAtPicker
            value={editedPost?.scheduledAt}
            onChange={onScheduleChange}
          />
        )}
        <div className={styles.buttons}>
          <Button
            title={t("Cancel")}
            buttonType="secondary-outline"
            onClickFunc={onCancel}
          />
          <Button
            title={t("Update")}
            buttonType="primary"
            onClickFunc={handleUpdate}
          />
        </div>
      </div>
    </div>
  );
};

export default EditPost;
